import { Link } from "react-router-dom";
import styled from "styled-components";

export const Wrapper = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: 12,
}));

export const SettingsBlock = styled("div")(({ theme }) => ({
  position: "relative",
}));

export const LanguageButton = styled("button")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  gap: "6px",
  backgroundColor: theme.palette.white,
  border: `1px solid ${theme.palette.header.primary}`,
  borderRadius: "8px",
  color: theme.palette.header.primary,
  fontSize: "16px",
  padding: "8px 16px",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "& .chevron": {
    fontSize: "12px",
    transition: "transform 0.2s ease",
  },

  "&:hover": {
    backgroundColor: theme.palette.header.hoverBg,

    "& .chevron": {
      transform: "translateY(2px)",
    },
  },
}));

export const UserIconButton = styled("button")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  width: "42px",
  height: "42px",
  backgroundColor: theme.palette.header.primary,
  border: "none",
  borderRadius: "8px",
  color: theme.palette.white,
  fontSize: "20px",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.header.primaryDark,
  },
}));

export const LanguageText = styled("span")(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.02em",
}));

export const UserInfoSection = styled("button")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: "12px",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "12px",
  padding: "6px 8px 6px 16px",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.header.hoverBg,
  },
}));

export const BalanceSection = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  alignItems: "flex-end",
  gap: "2px",
}));

export const UserName = styled("span")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.header.text,
  letterSpacing: "0.01em",
}));

export const BalanceLabel = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: "4px",
}));

export const BalanceAmount = styled("span")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "0.02em",

  "&.positive": {
    color: theme.palette.header.success,
  },

  "&.negative": {
    color: theme.palette.header.danger,
  },
}));

export const CreditText = styled("span")(({ theme }) => ({
  fontSize: "11px",
  color: theme.palette.header.textSecondary,
  fontWeight: 500,
}));

export const Avatar = styled("div")(({ theme }) => ({
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${theme.palette.header.avatarGradientStart} 0%, ${theme.palette.header.avatarGradientEnd} 100%)`,
  color: theme.palette.white,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "15px",
  fontWeight: 700,
  letterSpacing: "0.5px",
  boxShadow: `0 4px 12px ${theme.palette.header.avatarShadow}`,
  transition: "all 0.25s ease",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)",
  },

  "&.guest": {
    background: theme.palette.header.hoverBg,
    color: theme.palette.header.textMuted,
    boxShadow: "none",

    "&::after": {
      display: "none",
    },
  },

  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: `0 6px 16px ${theme.palette.header.avatarShadowHover}`,
  },
}));

export const AvatarImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "12px",
}));

export const DropdownItems = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "4px 0",
}));

export const DropdownSectionTitle = styled("div")(({ theme }) => ({
  padding: "8px 16px 6px",
  color: theme.palette.header.textSecondary,
  fontWeight: 600,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",

  "&.user-info": {
    color: theme.palette.header.text,
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: "0",
    paddingBottom: "2px",
  },

  "&.balance": {
    color: theme.palette.header.success,
    fontSize: "15px",
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: "0",
    paddingTop: "0",
    paddingBottom: "8px",
    borderBottom: `1px solid ${theme.palette.header.dropdownDivider}`,
    marginBottom: "4px",
  },
}));

export const DropdownItem = styled("div")(({ theme }) => ({
  padding: "10px 16px",
  color: theme.palette.header.dropdownItemText,
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s ease",
  borderRadius: "6px",
  margin: "0 4px",

  "&:hover": {
    backgroundColor: theme.palette.header.hoverBg,
    color: theme.palette.header.primary,
  },
}));

export const DropdownItemLink = styled(Link)(({ theme }) => ({
  display: "block",
  padding: "10px 16px",
  color: theme.palette.header.dropdownItemText,
  fontSize: "14px",
  fontWeight: 500,
  textDecoration: "none",
  transition: "all 0.15s ease",
  borderRadius: "6px",
  margin: "0 4px",

  "&:hover": {
    backgroundColor: theme.palette.header.hoverBg,
    color: theme.palette.header.primary,
  },
}));

export const LogoutButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "calc(100% - 8px)",
  margin: "4px",
  padding: "10px 16px",
  backgroundColor: "transparent",
  border: "none",
  borderTop: `1px solid ${theme.palette.header.dropdownDivider}`,
  color: theme.palette.header.danger,
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s ease",
  borderRadius: "0 0 6px 6px",
  marginTop: "4px",
  paddingTop: "12px",

  "& svg": {
    fontSize: "18px",
  },

  "&:hover": {
    backgroundColor: theme.palette.header.dangerBg,
    color: theme.palette.header.dangerDark,
  },
}));

export const TextLanguage = styled("div")(({ theme }) => ({
  color: theme.palette.header.dropdownItemText,
  fontSize: "14px",
  fontWeight: 500,
  position: "relative",
  transition: "color 0.15s ease",

  "&:hover": {
    color: theme.palette.header.primary,
  },

  "&.active": {
    color: theme.palette.header.primary,
    fontWeight: 600,
  },
}));

// Legacy exports for backward compatibility
export const GroupTitle = DropdownSectionTitle;
export const Item = DropdownItem;
export const ItemLink = DropdownItemLink;
export const Items = DropdownItems;
export const IconButton = LanguageButton;
export const UserSection = UserInfoSection;
export const UserBalance = BalanceAmount;
export const UserCredit = CreditText;
export const LanguageCurrency = LanguageText;

// Иконка «Поддержка» в шапке на мобильном (вкладки шапки там скрыты); бейдж непрочитанных — в углу
export const SupportIconLink = styled(Link)(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  position: "relative",
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  color: theme.palette.header.textMuted,
  fontSize: "22px",
  textDecoration: "none",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.header.hoverBg,
    color: theme.palette.header.primary,
  },

  "& .support-badge": {
    position: "absolute",
    top: 2,
    right: 0,
    pointerEvents: "none",
  },
}));
